/**
 * Anchor Bullion Sell Page JavaScript
 * Handles pricing API, estimator with product dropdowns, forms, and UI interactions
 */
(function () {
  'use strict';

  // Configuration
  const CONFIG = {
    API_REFRESH_INTERVAL: 3600000, // 1 hour
    MAX_RETRIES: 3,
    RETRY_DELAY: 2000,
    CONVERSIONS: {
      TROY_OZ_TO_GRAMS: 31.1034768,
      DWT_TO_TROY_OZ: 0.05,
    },
  };

  // State management
  const state = {
    currentPrices: null,
    products: {},
    retryCount: 0,
    apiInterval: null,
    selectedProduct: null,
  };

  // DOM Elements
  const elements = {
    wrapper: null,
    estimator: null,
    tabs: null,
    faqItems: null,
    mobileBar: null,
    quoteForm: null,
  };

function init() {
  if (!document.querySelector('.ab-sell-wrapper')) return;

  cacheElements();
  loadProducts();
  setupEventListeners();
  initializePricing();
  initializeTabs();      
  initializeFAQ();       
  initializeMobileBar(); 
  initializeForms();     

  safeSyncEstimatorUI();
}
  function cacheElements() {
    elements.wrapper  = document.querySelector('.ab-sell-wrapper');
    elements.estimator = document.querySelector('[data-estimator-root]') || document;
    elements.tabs      = document.querySelectorAll('.ab-tab-btn');
    elements.faqItems  = document.querySelectorAll('.ab-faq-item');
    elements.mobileBar = document.querySelector('.ab-mobile-bar');
    elements.quoteForm = document.querySelector('[data-quote-form]');
  }

  function loadProducts() {
    const productsElement = document.getElementById('ab-products-data');
    if (productsElement) {
      try {
        state.products = JSON.parse(productsElement.textContent);
      } catch (e) {
        console.error('Failed to load products JSON:', e);
        state.products = { gold: [], silver: [], platinum: [], palladium: [] };
      }
    }
  }

  function setupEventListeners() {
    // Look up controls globally so markup can live anywhere in the section
    const metalSelect     = document.querySelector('[data-metal-select]');
    const productSelect   = document.querySelector('[data-product-select]');
    const calculateBtn    = document.querySelector('[data-calculate-btn]');
    const recalcBtn       = document.querySelector('[data-recalculate]');
    const requestQuoteBtn = document.querySelector('[data-request-quote]');
    const puritySelect    = document.querySelector('[data-purity-select]');
    const purityCustom    = document.querySelector('[data-purity-custom]');

    if (metalSelect)      metalSelect.addEventListener('change', handleMetalChange);
    if (productSelect)    productSelect.addEventListener('change', handleProductChange);
    if (calculateBtn)     calculateBtn.addEventListener('click', handleCalculate);
    if (recalcBtn)        recalcBtn.addEventListener('click', handleRecalculate);
    if (requestQuoteBtn)  requestQuoteBtn.addEventListener('click', handleRequestQuote);

    if (puritySelect) {
      puritySelect.addEventListener('change', function () {
        if (purityCustom) {
          purityCustom.style.display = this.value === 'custom' ? 'block' : 'none';
        }
      });
    }

    // Page visibility
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  function safeSyncEstimatorUI() {
    const metalSelect   = document.querySelector('[data-metal-select]');
    const productSelect = document.querySelector('[data-product-select]');
    if (!productSelect) return;

    const hasMetal = metalSelect && metalSelect.value;
    if (!hasMetal) {
      productSelect.disabled = true;
      productSelect.innerHTML = '<option value="">First select a metal type</option>';
    } else {
      handleMetalChange({ target: metalSelect });
    }
  }

  function handleMetalChange(e) {
    const metal = e.target.value; // must be: gold|silver|platinum|palladium
    const productSelect = document.querySelector('[data-product-select]');
    const customProduct = document.querySelector('[data-custom-product]');
    if (!productSelect) return;

    productSelect.innerHTML = '';

    if (!metal) {
      productSelect.innerHTML = '<option value="">First select a metal type</option>';
      productSelect.disabled = true;
      if (customProduct) customProduct.style.display = 'none';
      return;
    }

    productSelect.disabled = false;
    productSelect.innerHTML = '<option value="">Select a product</option>';

    const products = state.products[metal] || [];
    products.forEach((product) => {
      const option = document.createElement('option');
      option.value = JSON.stringify(product);
      option.textContent = product.name;
      productSelect.appendChild(option);
    });

    const customOption = document.createElement('option');
    customOption.value = 'custom';
    customOption.textContent = 'Add My Own Product';
    productSelect.appendChild(customOption);

    if (customProduct) customProduct.style.display = 'none';
  }

  function handleProductChange(e) {
    const value = e.target.value;
    const customProduct = document.querySelector('[data-custom-product]');

    if (value === 'custom') {
      if (customProduct) customProduct.style.display = 'block';
      state.selectedProduct = null;
    } else if (value) {
      if (customProduct) customProduct.style.display = 'none';
      try {
        state.selectedProduct = JSON.parse(value);
      } catch {
        state.selectedProduct = null;
      }
    } else {
      if (customProduct) customProduct.style.display = 'none';
      state.selectedProduct = null;
    }
  }

  async function initializePricing() {
    const apiUrl = elements.wrapper && elements.wrapper.dataset.apiUrl;
    if (!apiUrl) {
      console.warn('No pricing API URL configured');
      state.currentPrices = { gold: 2000, silver: 25, platinum: 1000, palladium: 1500 };
      return;
    }
    await fetchPrices();
    startPriceRefresh();
  }

  async function fetchPrices() {
    const apiUrl = elements.wrapper && elements.wrapper.dataset.apiUrl;
    if (!apiUrl) return;
    try {
      const response = await fetchWithRetry(apiUrl);
      const data = await response.json();
      state.currentPrices = processPriceData(data);
      state.retryCount = 0;
    } catch (error) {
      console.error('Failed to fetch prices:', error);
      handlePriceError();
    }
  }

  async function fetchWithRetry(url, retries = CONFIG.MAX_RETRIES) {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response;
      } catch (error) {
        if (i === retries - 1) throw error;
        await new Promise((r) => setTimeout(r, CONFIG.RETRY_DELAY * Math.pow(2, i)));
      }
    }
  }

  function processPriceData(data) {
  // Accept multiple shapes:
  // A) Pipedream: { metals: { gold: { mid }, silver: { mid }, ... } }
  // B) Ticker-like: { XAUUSD: { mid }, XAGUSD: { mid }, XPTUSD: { mid }, XPDUSD: { mid } }
  // C) Flat: { gold: number | { mid|price }, silver: ... }

  const pickMid = (node) => {
    if (node == null) return null;
    if (typeof node === 'number') return node;
    const cand = node.mid ?? node.price ?? node.rate ?? node.value ?? node.last;
    return typeof cand === 'number' ? cand : null;
  };

  let gold = null, silver = null, platinum = null, palladium = null;

  // A) Pipedream
  if (data && typeof data.metals === 'object') {
    gold      = pickMid(data.metals.gold);
    silver    = pickMid(data.metals.silver);
    platinum  = pickMid(data.metals.platinum);
    palladium = pickMid(data.metals.palladium);
  }

  // B) Tickers
  if (gold      == null && data?.XAUUSD) gold      = pickMid(data.XAUUSD);
  if (silver    == null && data?.XAGUSD) silver    = pickMid(data.XAGUSD);
  if (platinum  == null && data?.XPTUSD) platinum  = pickMid(data.XPTUSD);
  if (palladium == null && data?.XPDUSD) palladium = pickMid(data.XPDUSD);

  // C) Flat metals at root
  if (gold      == null && data?.gold)      gold      = pickMid(data.gold);
  if (silver    == null && data?.silver)    silver    = pickMid(data.silver);
  if (platinum  == null && data?.platinum)  platinum  = pickMid(data.platinum);
  if (palladium == null && data?.palladium) palladium = pickMid(data.palladium);

  // Defaults if anything still missing
  return {
    gold:      gold      ?? 2000,
    silver:    silver    ?? 25,
    platinum:  platinum  ?? 1000,
    palladium: palladium ?? 1500
  };
}


  function startPriceRefresh() {
    stopPriceRefresh();
    state.apiInterval = setInterval(fetchPrices, CONFIG.API_REFRESH_INTERVAL);
  }

  function stopPriceRefresh() {
    if (state.apiInterval) {
      clearInterval(state.apiInterval);
      state.apiInterval = null;
    }
  }

  function handleVisibilityChange() {
    if (document.hidden) {
      stopPriceRefresh();
    } else {
      fetchPrices();
      startPriceRefresh();
    }
  }

  function handlePriceError() {
    state.retryCount++;
    if (state.retryCount < CONFIG.MAX_RETRIES) {
      setTimeout(fetchPrices, CONFIG.RETRY_DELAY * state.retryCount);
    }
  }

  function handleCalculate() {
    const formData = getEstimatorFormData();
    if (!validateEstimatorForm(formData)) return;
    const calculation = calculatePayout(formData);
    displayResults(calculation);
  }

  function getEstimatorFormData() {
    const form = elements.estimator;
    const metalSelect = form.querySelector('[data-metal-select]');
    const productSelect = form.querySelector('[data-product-select]');

    const data = { metal: metalSelect?.value };

    if (productSelect?.value === 'custom') {
      data.isCustom = true;
      data.name = form.querySelector('[data-custom-name]')?.value || 'Custom Item';
      data.weight = parseFloat(form.querySelector('[data-custom-weight]')?.value) || 0;
      data.weightUnit = form.querySelector('[data-unit-select]')?.value || 'oz';
      data.purity = getPurityValue();
      data.quantity = parseInt(form.querySelector('[data-quantity]')?.value) || 1;
      data.type = form.querySelector('[data-custom-type]')?.value || 'other';
    } else if (state.selectedProduct) {
      data.isCustom = false;
      data.name = state.selectedProduct.name;
      data.weight = state.selectedProduct.weight;
      data.weightUnit = 'oz';
      data.purity = state.selectedProduct.purity;
      data.quantity = parseInt(form.querySelector('[data-quantity]')?.value) || 1;
      data.type = state.selectedProduct.type;
      data.pricingMethod = state.selectedProduct.pricingMethod;
      data.payoutPercentage = state.selectedProduct.payoutPercentage;
      data.dollarAmount = state.selectedProduct.dollarAmount;
    }
    return data;
  }

  function getPurityValue() {
    const puritySelect = elements.estimator.querySelector('[data-purity-select]');
    const purityCustom = elements.estimator.querySelector('[data-purity-custom]');
    if (puritySelect?.value === 'custom' && purityCustom) {
      return (parseFloat(purityCustom.value) || 0) / 100;
    }
    return parseFloat(puritySelect?.value) || 0.999;
  }

  function validateEstimatorForm(data) {
    const errors = [];
    if (!data.metal) errors.push('Please select a metal type');
    if (!data.name) errors.push('Please select or describe a product');
    if (!data.weight || data.weight <= 0) errors.push('Please enter a valid weight');

    if (errors.length > 0) {
      showEstimatorError(errors.join('. '));
      return false;
    }
    if (!state.currentPrices) {
      showEstimatorError('Prices are loading. Please wait.');
      return false;
    }
    return true;
  }

  function calculatePayout(data) {
    let weightInOz = data.weight;
    if (data.weightUnit === 'g') {
      weightInOz = data.weight / CONFIG.CONVERSIONS.TROY_OZ_TO_GRAMS;
    } else if (data.weightUnit === 'dwt') {
      weightInOz = data.weight * CONFIG.CONVERSIONS.DWT_TO_TROY_OZ;
    }

    const totalWeight = weightInOz * (data.quantity || 1);
    const spotPrice = state.currentPrices[data.metal] || 0;
    const fineWeight = totalWeight * data.purity;
    const meltValue = fineWeight * spotPrice;

    let estimatedPayout;
    if (data.pricingMethod === 'dollar_over') {
      const dollarAmount = parseFloat(data.dollarAmount) || 0;
      estimatedPayout = meltValue + dollarAmount * fineWeight;
    } else if (data.pricingMethod === 'dollar_under') {
      const dollarAmount = parseFloat(data.dollarAmount) || 0;
      estimatedPayout = meltValue - dollarAmount * fineWeight;
    } else {
      let payoutFactor;
      if (data.payoutPercentage) {
        payoutFactor = data.payoutPercentage / 100;
      } else {
        const factorKey = `${data.metal}${data.type.charAt(0).toUpperCase()}${data.type.slice(1)}Factor`;
        const payoutFactorPercent = parseFloat(elements.wrapper.dataset[factorKey]) || 90;
        payoutFactor = payoutFactorPercent / 100;
      }
      estimatedPayout = meltValue * payoutFactor;
    }

    estimatedPayout = Math.max(0, estimatedPayout);

    return {
      spotPrice,
      fineWeight,
      meltValue,
      estimatedPayout,
      metal: data.metal,
      name: data.name,
      quantity: data.quantity,
    };
  }

  function displayResults(calculation) {
    const resultsPanel = elements.estimator.querySelector('[data-results-panel]');
    if (!resultsPanel) return;

    updateResultValue('[data-spot-price]', `$${calculation.spotPrice.toFixed(2)}/oz`);
    updateResultValue('[data-fine-weight]', `${calculation.fineWeight.toFixed(4)} oz`);
    updateResultValue('[data-melt-value]', `$${calculation.meltValue.toFixed(2)}`);
    updateResultValue('[data-estimated-payout]', `$${calculation.estimatedPayout.toFixed(2)}`);

    resultsPanel.style.display = 'block';

    const form = elements.estimator.querySelector('.ab-estimator-form');
    if (form) form.style.display = 'none';

    resultsPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function updateResultValue(selector, value) {
    const element = elements.estimator.querySelector(selector);
    if (element) element.textContent = value;
  }

  function handleRecalculate() {
    const resultsPanel = elements.estimator.querySelector('[data-results-panel]');
    if (resultsPanel) resultsPanel.style.display = 'none';

    const form = elements.estimator.querySelector('.ab-estimator-form');
    if (form) form.style.display = 'block';

    const errorPanel = elements.estimator.querySelector('[data-error-panel]');
    if (errorPanel) errorPanel.style.display = 'none';
  }

  function handleRequestQuote() {
  const quoteForm = document.getElementById('ab-quote-form');
  if (quoteForm) {
    quoteForm.scrollIntoView({ behavior: 'smooth' });
  }
}

  function handleTabClick(e) {
    const tab = e.currentTarget;
    const targetId = tab.getAttribute('aria-controls');

    elements.tabs.forEach((t) => {
      t.setAttribute('aria-selected', 'false');
      t.classList.remove('ab-tab-active');
    });

    tab.setAttribute('aria-selected', 'true');
    tab.classList.add('ab-tab-active');

    document.querySelectorAll('.ab-tab-panel').forEach((panel) => {
      panel.hidden = panel.id !== targetId;
      panel.classList.toggle('ab-panel-active', panel.id === targetId);
    });
  }

  function handleTabKeydown(e) {
    const tabs = Array.from(elements.tabs);
    const currentIndex = tabs.indexOf(e.currentTarget);
    let newIndex;

    switch (e.key) {
      case 'ArrowLeft':
        newIndex = currentIndex - 1;
        if (newIndex < 0) newIndex = tabs.length - 1;
        break;
      case 'ArrowRight':
        newIndex = currentIndex + 1;
        if (newIndex >= tabs.length) newIndex = 0;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = tabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    tabs[newIndex].focus();
    tabs[newIndex].click();
  }
function initializeTabs() {
  elements.tabs.forEach((tab) => {
    tab.addEventListener('click', handleTabClick);
    tab.addEventListener('keydown', handleTabKeydown);
  });
}
  function initializeFAQ() {
    elements.faqItems.forEach((item) => {
      const button = item.querySelector('.ab-faq-question');
      if (button) {
        button.addEventListener('click', handleFAQClick);
      }
    });
  }

  function handleFAQClick(e) {
    const button = e.currentTarget;
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const answerId = button.getAttribute('aria-controls');
    const answer = document.getElementById(answerId);

    button.setAttribute('aria-expanded', !isExpanded);
    if (answer) {
      answer.hidden = isExpanded;
    }
  }

  function initializeMobileBar() {
    if (!elements.mobileBar) return;

    let lastScroll = 0;
    let ticking = false;

    function updateMobileBar() {
      const currentScroll = window.pageYOffset;

      if (currentScroll > 100 && currentScroll > lastScroll) {
        elements.mobileBar.style.transform = 'translateY(100%)';
      } else {
        elements.mobileBar.style.transform = 'translateY(0)';
      }

      lastScroll = currentScroll;
      ticking = false;
    }

    function requestTick() {
      if (!ticking) {
        requestAnimationFrame(updateMobileBar);
        ticking = true;
      }
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReducedMotion) {
      window.addEventListener('scroll', requestTick, { passive: true });
    }
  }

  function initializeForms() {
    if (elements.quoteForm) {
      elements.quoteForm.addEventListener('submit', handleQuoteSubmit);
    }
  }

  async function handleQuoteSubmit(e) {
  e.preventDefault();

  const form = e.target;
  const submitBtn = form.querySelector('.ab-submit-btn');
  const btnText = form.querySelector('.ab-btn-text');
  const btnLoading = form.querySelector('.ab-btn-loading');
  const successMsg = form.querySelector('.ab-form-success');
  const errorMsg = form.querySelector('.ab-form-error');

  if (submitBtn) submitBtn.disabled = true;
  if (btnText) btnText.style.display = 'none';
  if (btnLoading) btnLoading.style.display = 'inline';
  if (successMsg) successMsg.style.display = 'none';
  if (errorMsg) errorMsg.style.display = 'none';

  try {
    const formData = new FormData();
    
    // Add Shopify required fields
    formData.append('form_type', 'contact');
    formData.append('utf8', '✓');
    
    // Get form values and map to Shopify format
    const name = form.querySelector('[name="name"]').value;
    const email = form.querySelector('[name="email"]').value;
    const phone = form.querySelector('[name="phone"]').value;
    const items = form.querySelector('[name="items"]').value;
    const preferred = form.querySelector('[name="preferred_contact"]').value;
    
    formData.append('contact[name]', name);
    formData.append('contact[email]', email);
    formData.append('contact[phone]', phone);
    formData.append('contact[body]', `Items: ${items}\n\nPreferred Contact: ${preferred}\nPhone: ${phone}`);
    
    // Submit via AJAX
    const response = await fetch('/contact', {
      method: 'POST',
      body: formData
    });

    // Shopify returns a redirect, so we just assume success if no error
    if (successMsg) successMsg.style.display = 'flex';
    form.reset();
    
  } catch (error) {
    console.error('Quote submission error:', error);
    showFormMessage(errorMsg, 'There was an error. Please try again or call us.');
  } finally {
    if (submitBtn) submitBtn.disabled = false;
    if (btnText) btnText.style.display = 'inline';
    if (btnLoading) btnLoading.style.display = 'none';
  }
}

  function showFormMessage(element, message) {
    if (!element) return;
    const textElement = element.querySelector('[data-error-text]') || element;
    if (textElement) textElement.textContent = message;
    element.style.display = 'block';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
